// Parametric 2D cutout skeleton. A RigPose (hip position, pelvis/torso/head
// rotations, ankle targets) is solved into bone transforms with two-bone IK
// for the legs. The same Skeleton drives the painter AND the attachment-point
// metadata, so equipment and arms can never drift from the drawn body.
//
// Conventions (right-facing, screen space, y down):
//   - Angles in radians, 0 = pointing right, positive = clockwise.
//   - A bone transform (Xf) maps part-local coordinates into the cell:
//     p_cell = (x, y) + R(rot) · p_local.
//   - Part-local frames are authored in the neutral upright pose: legs extend
//     along +y from their joint, the torso and head extend along −y. So rot is
//     the rotation away from that neutral pose (0 = upright / unrotated), which
//     is exactly the rotation a rigidly attached module needs.

import { FOOT_ANCHOR } from '../../config/constants';
import type { AttachKey, AttachPoint } from '../../core/types';
import type { MechStyle, Pt } from './styles';

/**
 * Visible ink below a shape edge: half the per-shape stroke plus the silhouette
 * dilation. Planted soles are lifted by this much so the drawn outline (not the
 * fill) rests exactly on the ground line.
 */
export const INK_TOTAL = 2.1;
/** y of the sole edge for a planted foot. */
export const SOLE_LINE = FOOT_ANCHOR.y - INK_TOTAL;

export interface Xf {
  x: number;
  y: number;
  rot: number;
}

/** Ankle target (cell px) and foot pitch (positive = toe down). */
export interface LegTarget {
  x: number;
  y: number;
  a: number;
}

export interface RigPose {
  /** Pelvis centre in cell px. */
  hx: number;
  hy: number;
  pelvis: number;
  /** Torso lean, pivoting at the pelvis centre (positive = forward). */
  torso: number;
  /** Absolute head rotation (keeps the gaze stable independent of the torso). */
  head: number;
  legF: LegTarget;
  legR: LegTarget;
}

export interface LegSolved {
  hip: Pt;
  knee: Pt;
  ankle: Pt;
  thigh: Xf;
  shin: Xf;
  foot: Xf;
  /** False if the target was out of reach (foot would float). */
  reached: boolean;
  /** Lowest sole point (shape edge, cell px). */
  soleY: number;
}

export interface Skeleton {
  pose: RigPose;
  pelvis: Xf;
  torso: Xf;
  head: Xf;
  legF: LegSolved;
  legR: LegSolved;
}

export function apply(xf: Xf, p: Pt): [number, number] {
  const c = Math.cos(xf.rot);
  const s = Math.sin(xf.rot);
  return [xf.x + p[0] * c - p[1] * s, xf.y + p[0] * s + p[1] * c];
}

/** Lowest sole offset below the ankle for a foot pitched by a (toe and heel corners). */
export function soleDrop(style: MechStyle, a: number): number {
  const s = Math.sin(a);
  const c = Math.cos(a);
  const toe = style.toe * s + style.ankleH * c;
  const heel = -style.heel * s + style.ankleH * c;
  return Math.max(toe, heel);
}

/** Ankle y that puts the lowest sole point `lift` px above the planted sole line. */
export function ankleYForLift(style: MechStyle, a: number, lift: number): number {
  return SOLE_LINE - Math.max(0, lift) - soleDrop(style, a);
}

/** Two-bone IK with the knee bending forward (+x side of the hip→ankle line for an upright leg). */
function solveLeg(style: MechStyle, hip: Pt, target: LegTarget): LegSolved {
  const l1 = style.thigh;
  const l2 = style.shin;
  const dx = target.x - hip[0];
  const dy = target.y - hip[1];
  const d = Math.hypot(dx, dy);
  const maxReach = l1 + l2 - 0.01;
  const minReach = Math.abs(l1 - l2) + 0.5;
  const reached = d <= maxReach + 0.05;
  const dc = Math.min(maxReach, Math.max(minReach, d));
  const base = Math.atan2(dy, dx);
  const cosA = (l1 * l1 + dc * dc - l2 * l2) / (2 * l1 * dc);
  const a = Math.acos(Math.max(-1, Math.min(1, cosA)));
  const thighAng = base - a;
  const knee: Pt = [hip[0] + Math.cos(thighAng) * l1, hip[1] + Math.sin(thighAng) * l1];
  const ankle: Pt = reached
    ? [target.x, target.y]
    : [hip[0] + Math.cos(base) * dc, hip[1] + Math.sin(base) * dc];
  const shinAng = Math.atan2(ankle[1] - knee[1], ankle[0] - knee[0]);
  return {
    hip,
    knee,
    ankle,
    thigh: { x: hip[0], y: hip[1], rot: thighAng - Math.PI / 2 },
    shin: { x: knee[0], y: knee[1], rot: shinAng - Math.PI / 2 },
    foot: { x: ankle[0], y: ankle[1], rot: target.a },
    reached,
    soleY: ankle[1] + soleDrop(style, target.a),
  };
}

export function solve(style: MechStyle, pose: RigPose): Skeleton {
  const pelvis: Xf = { x: pose.hx, y: pose.hy, rot: pose.pelvis };
  const torso: Xf = { x: pose.hx, y: pose.hy, rot: pose.torso };
  const neck = apply(torso, style.torso.neck);
  const head: Xf = { x: neck[0], y: neck[1], rot: pose.head };
  const hipF = apply(pelvis, [style.hipSpread, style.hipJointY]);
  const hipR = apply(pelvis, [-style.hipSpread, style.hipJointY]);
  return {
    pose,
    pelvis,
    torso,
    head,
    legF: solveLeg(style, hipF, pose.legF),
    legR: solveLeg(style, hipR, pose.legR),
  };
}

const r2 = (v: number): number => Math.round(v * 100) / 100;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;

function point(p: Pt, rot: number): AttachPoint {
  return { x: r2(p[0]), y: r2(p[1]), rot: r3(rot) };
}

/**
 * Attachment points for a solved frame. Positions are cell px (right-facing);
 * rot is the rotation of the carrying body part away from its neutral upright
 * pose, so a module authored on the neutral pose and drawn with rotation = rot
 * follows the limb.
 *   head      head centre            (head frame)
 *   chest     chest mount, ~6 px behind the front plate at reactor height (torso)
 *   back      back mount on the outer surface of the backpack (torso)
 *   shoulderF/shoulderR  arm pivots (torso frame; modules sit above them)
 *   hip       pelvis centre          (pelvis)
 *   shinF/R   middle of the shin bone (shin frame, 0 = vertical)
 *   footF/R   sole centre             (foot pitch)
 */
export function attachPoints(style: MechStyle, sk: Skeleton): Record<AttachKey, AttachPoint> {
  const t = style.torso;
  const shin = (leg: LegSolved): AttachPoint =>
    point([(leg.knee[0] + leg.ankle[0]) / 2, (leg.knee[1] + leg.ankle[1]) / 2], leg.shin.rot);
  const foot = (leg: LegSolved): AttachPoint =>
    point(apply(leg.foot, [(style.toe - style.heel) / 2, style.ankleH]), leg.foot.rot);
  return {
    head: point(apply(sk.head, style.headCenter), sk.head.rot),
    chest: point(apply(sk.torso, t.chest), sk.torso.rot),
    back: point(apply(sk.torso, t.back), sk.torso.rot),
    shoulderF: point(apply(sk.torso, t.shoulderF), sk.torso.rot),
    shoulderR: point(apply(sk.torso, t.shoulderR), sk.torso.rot),
    hip: point([sk.pelvis.x, sk.pelvis.y], sk.pelvis.rot),
    shinF: shin(sk.legF),
    shinR: shin(sk.legR),
    footF: foot(sk.legF),
    footR: foot(sk.legR),
  };
}
